import type { Entitlement } from '@/shared/entitlements';

import { GateRequirement, SpeedyFlowUser } from '../types';

/**
 * Entitlement: whether the visitor may pass a gate, and how long to keep asking after a payment.
 *
 * These sit together because they are the same question asked at two moments. A purchase is
 * confirmed when the gate that opened the flow would now let the visitor through — never by a
 * Stripe redirect or a closed tab, both of which race the webhook that does the granting
 * (ADR 0003).
 */

/**
 * Whether the visitor already satisfies the gate.
 *
 * Asked through `can`, like every other gate in the app, so the flow and the page behind it cannot
 * disagree. A role is answered by what it holds: its viewing set everywhere, its habitats in scope.
 */
export const satisfiesGate = (gate: GateRequirement, user: SpeedyFlowUser): boolean => {
  if (!user.isLoggedIn) {
    return false;
  }
  if (gate.kind === 'account') {
    return true;
  }
  if (gate.kind === 'verified-email') {
    return user.isRealEmail;
  }
  if (gate.kind === 'entitlement') {
    return !!gate.entitlement && user.can(gate.entitlement, gate.entitlementId);
  }
  return !!gate.habitatId && user.can('habitat.access', gate.habitatId);
};

/** What the pair endpoint is asked to grant for this gate, if it names anything. */
export const entitlementOf = (gate: GateRequirement | undefined): Entitlement | undefined => {
  if (gate?.kind === 'entitlement') return gate.entitlement;
  if (gate?.kind === 'habitat-access') return 'habitat.access';
  return undefined;
};

/**
 * The budget for re-checking the entitlement after a payment. Exhausting it is not a failure —
 * the card was charged, so the modal says "still finalising" and offers a retry.
 */
export const ENTITLEMENT_POLL_BUDGET_MS = 45_000;

/**
 * How long to wait before each re-check, in milliseconds.
 *
 * The webhook usually beats the visitor back, so the first check normally succeeds and the rest
 * never run. Backing off keeps the rare slow case bounded without spending requests on the common
 * fast one. The last wait is clamped to whatever budget remains rather than being dropped whole,
 * so the schedule actually uses the budget it was given.
 */
export const entitlementPollDelays = (budgetMs: number = ENTITLEMENT_POLL_BUDGET_MS): number[] => {
  const delays: number[] = [];
  let delay = 1_000;
  let spent = 0;
  while (spent < budgetMs) {
    const next = Math.min(delay, budgetMs - spent);
    delays.push(next);
    spent += next;
    delay *= 2;
  }
  return delays;
};
